import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import VendorLayout from "@/components/vendor_portal/VendorLayout";

export default function VendorMessagesPage() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/vendor/messages/threads")
      .then(({ data }) => setThreads(data.threads || []))
      .catch((err) => setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || ""))
      .finally(() => setLoading(false));
  }, []);

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  return (
    <VendorLayout>
      <section data-testid="vendor-messages-page">
        <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">Messages</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-3">
          Threaded Conversations
          {totalUnread > 0 && (
            <span className="ml-3 inline-block px-3 py-1 text-xs uppercase tracking-widest bg-[#1A422D] text-white align-middle" data-testid="vendor-msg-unread-total">
              {totalUnread} unread
            </span>
          )}
        </h1>
        <p className="text-sm text-[#7A7A7A] mt-3 max-w-2xl">
          Every conversation you're having with a pro shop — organised by order.
          Open a thread to reply, attach artwork, or confirm ship dates.
        </p>

        {error && (
          <div className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">{error}</div>
        )}
        {loading && <div className="mt-8 text-sm text-[#7A7A7A]">Loading…</div>}

        {!loading && threads.length === 0 && (
          <div className="mt-10 p-10 bg-white border border-[#E5E3D8] max-w-2xl text-center" data-testid="vendor-msg-empty">
            <MessageSquare size={32} className="mx-auto text-[#B59A5A]" />
            <p className="mt-4 text-[#4A4A4A]">No conversations yet. Once you post the first message on any request, it will appear here.</p>
          </div>
        )}

        {threads.length > 0 && (
          <div className="mt-8 border border-[#E5E3D8] bg-white divide-y divide-[#F0EDE0]" data-testid="vendor-msg-thread-list">
            {threads.map((t) => (
              <Link key={t.order_id} to={`/vendor/requests/${t.order_id}`}
                    data-testid={`vendor-msg-thread-${t.order_id}`}
                    className="flex items-start justify-between gap-4 px-5 py-4 hover:bg-[#F9F8F4] transition-colors">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-sm text-[#1A422D]">{t.display_order_id}</div>
                    {t.unread > 0 && (
                      <span className="inline-block px-2 py-0.5 text-[9px] uppercase tracking-widest bg-[#B59A5A] text-white">
                        {t.unread} new
                      </span>
                    )}
                  </div>
                  <div className="text-[#1A1A1A] mt-0.5">{t.item_name || "(Order)"}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#B59A5A]">{t.club_name}</div>
                  <div className="mt-2 text-sm text-[#4A4A4A] italic line-clamp-1">
                    <span className="text-[#7A7A7A] uppercase text-[9px] tracking-widest mr-1">
                      {t.last_message_role === "vendor" ? "You" : "Pro Shop"}:
                    </span>
                    {t.last_message_preview}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-[#7A7A7A]">{new Date(t.last_message_at).toLocaleString()}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#1A422D]">
                    Open <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </VendorLayout>
  );
}
